import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import Seo from '../components/Seo';
import HeaderSection from './HeaderSection';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';

// Brand primary blue (matches --color-primary token, see DESIGN_SYSTEM.md)
const LOGO_BLUE = '#2563eb';

// ─── SVG Icons ─────────────────────────────────────────────────────────────────
const PauseIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="6" y="4" width="4" height="16" fill="white" /><rect x="14" y="4" width="4" height="16" fill="white" /></svg>;
const PlayIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M8 5V19L19 12L8 5Z" fill="white" /></svg>;
const PrevIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const NextIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;

const LinkedInIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);
const MailIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2" /><path d="M22 7l-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── Slide Data ────────────────────────────────────────────────────────────────
const heroSlides = [
  {
    image: '/images/hero1.jpg',
    category: 'TotoWala Mission',
    title: 'Smarter Pricing for Everyone',
    desc: 'Bringing modern ride-booking to every doorstep - lowest commission, no hidden fees, lowest surge pricing. Drivers earn more here than anywhere else.',
  },
  {
    image: '/images/hero2.jpg',
    category: 'TotoWala Impact',
    title: 'Empowering Local Communities',
    desc: 'Specifically built for smaller cities and towns, understanding their unique needs and helping local communities thrive - starting from Bhagalpur, Bihar.',
  },
  {
    image: '/images/hero3.jpg',
    category: 'TotoWala Vision',
    title: 'Technology Without Barriers',
    desc: 'Making modern ride-booking technology accessible to everyone in small cities without any cost barrier. A driver-first ecosystem built for India.',
  },
];

const gallerySlides = [
  {
    image: '/images/gallery1.jpg',
    title: 'Driver-First Earnings',
    desc: 'We are built to serve people, not extract profit. Drivers earn more on TotoWala than on any other platform - lowest commission, no convenience fees.',
  },
  {
    image: '/images/gallery2.jpg',
    title: 'No Hidden Charges',
    desc: 'Complete transparency in pricing. What you see is what you pay - no surprise fees, no hidden charges, no platform fee.',
  },
  {
    image: '/images/gallery3.jpg',
    title: 'Tier-3 City Focus',
    desc: 'Prioritizing smaller cities with near-zero surge pricing. Toto, Auto, Cab, and Bike services seamlessly connected to local needs.',
  },
  {
    image: '/images/gallery4.jpg',
    title: 'Direct Payment',
    desc: 'Ensuring fair, direct transactions. Riders pay the drivers directly, reinforcing trust and removing middlemen from the earning loop.',
  },
];

// ─── Team Data ─────────────────────────────────────────────────────────────────
const TEAM = [
  {
    name: 'Namit Rai',
    role: 'Founder',
    dept: 'Leadership',
    bio: 'Namit is the founder and driving force behind TotoWala. With a vision to bring transparent, driver-first ride-booking to tier-3 cities, he leads strategy, product direction, and the company\'s long-term mission of zero-commission rides across India.',
    initials: 'NR',
    accentColor: LOGO_BLUE,
    social: { linkedin: 'https://www.linkedin.com/in/namitrai/', email: 'namit@totowala.in' },
  },
  {
    name: 'Tushar Anand',
    role: 'Co-Founder & CTO',
    dept: 'Engineering & Leadership',
    bio: 'Tushar is the Co-Founder and CTO of TotoWala, responsible for all technical decisions and advancing the technology stack. As the lead engineer, he drives the development of the TotoWala application, from mobile apps to backend infrastructure, shaping the architecture that powers the platform.',
    initials: 'TA',
    accentColor: LOGO_BLUE,
    social: { linkedin: 'https://www.linkedin.com/in/tusharanand2000/', email: 'tushar@totowala.in' },
  },
];

const VALUES = [
  {
    label: 'People Over Profit',
    desc: 'We will always prioritize the wellbeing of our drivers and riders over any financial metric. Transparent, honest, and fair - always.',
    accentColor: LOGO_BLUE,
  },
  {
    label: 'Radical Transparency',
    desc: 'No hidden fees. No fine print. No surprises. What you see in the TotoWala app is exactly what you get, every single time.',
    accentColor: LOGO_BLUE,
  },
  {
    label: 'Local First',
    desc: 'We are a Bhagalpur startup. We understand local needs, speak the local language, and serve the community before scaling.',
    accentColor: LOGO_BLUE,
  },
  {
    label: 'Move Fast, Stay Honest',
    desc: 'We build rapidly and iterate, but never at the expense of our core commitment - zero exploitation of drivers or riders.',
    accentColor: LOGO_BLUE,
  },
];

const HOW_WE_WORK = [
  {
    step: '01',
    title: 'Start with Empathy',
    desc: 'Every decision starts by asking: Does this make life better for the driver? Does this make the rider\'s journey easier and more affordable?',
  },
  {
    step: '02',
    title: 'Build in the Open',
    desc: 'We involve our driver community in product feedback loops. Real drivers, real feedback - before every major release and decision.',
  },
  {
    step: '03',
    title: 'Ship and Listen',
    desc: 'We launch fast, measure carefully, and iterate based on ground-level reality - not boardroom assumptions or industry benchmarks.',
  },
  {
    step: '04',
    title: 'Share the Wins',
    desc: 'When TotoWala grows, drivers earn more. Our success is always measured in improved livelihoods, not just revenue numbers.',
  },
];

const OPEN_ROLES = [
  { title: 'React Native Developer', dept: 'Engineering', type: 'Full-time' },
  { title: 'Backend Engineer (Node.js)', dept: 'Engineering', type: 'Full-time' },
  { title: 'City Operations Manager', dept: 'Operations', type: 'Full-time' },
  { title: 'Community Growth Associate', dept: 'Growth', type: 'Internship' },
];

const PERKS = [
  { label: 'Startup Experience',      desc: 'Get real-world experience building a product from the ground up at an early-stage startup.' },
  { label: 'Live Product Experience', desc: 'Work on a product that real users in Bihar depend on every day, not a side project.' },
  { label: 'Learning Budget',     desc: 'Courses, books and conferences. We invest in your professional growth.' },
  { label: 'Free TotoWala Rides', desc: 'Free rides on the platform while you are part of the team.' },
  { label: 'High-Impact Work',    desc: 'No make-work tasks. Every sprint directly moves Bihar\'s transport forward.' },
  { label: 'Portfolio Work',      desc: 'Build meaningful, production-grade work you can proudly showcase to future employers.' },
];

// ─── Intersection Observer Fade Hook ──────────────────────────────────────────
const useFadeIn = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const FadeIn = ({ children, delay = 0, className = '', style: extraStyle = {} }) => {
  const [ref, visible] = useFadeIn();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.55s ease ${delay}s, transform 0.55s ease ${delay}s`,
        ...extraStyle,
      }}
    >
      {children}
    </div>
  );
};

// ─── Hero Slider (UNCHANGED) ───────────────────────────────────────────────────
const HeroSlider = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setActiveSlide(prev => (prev + 1) % heroSlides.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <div id="hero-image-section" className="relative h-screen w-full overflow-hidden bg-black">
      {heroSlides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-[800ms] ${i === activeSlide ? 'opacity-100 pointer-events-auto z-10' : 'opacity-0 pointer-events-none z-0'
            }`}
        >
          <div className="absolute inset-0">
            <img src={slide.image} alt={slide.title} className="object-cover w-full h-full" />
          </div>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative h-full container mx-auto px-6 sm:px-12 flex flex-col justify-center max-w-7xl">
            <div className="text-white mb-6">
              <span className="text-sm font-bold tracking-widest uppercase mb-2 block">{slide.category}</span>
              <h1 className="text-5xl md:text-7xl font-bold leading-tight max-w-4xl" style={{ fontFamily: "'Fraunces',serif" }}>{slide.title}</h1>
            </div>
            <div className="flex">
              <div className="w-1 bg-yellow-400 mr-6 flex-shrink-0"></div>
              <div className="max-w-2xl text-white">
                <p className="text-lg md:text-xl mb-8" style={{ fontFamily: "'Figtree',sans-serif" }}>{slide.desc}</p>
                <a href="#team" className="group inline-flex items-center gap-3 bg-white text-black px-6 py-3 font-semibold hover:bg-gray-200 transition-colors" style={{ fontFamily: "'Figtree',sans-serif" }}>
                  <span>Meet the Team</span>
                  <span className="group-hover:translate-x-2 transition-transform duration-300">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Bottom-left controls */}
      <div className="absolute bottom-10 left-6 sm:left-12 z-20 flex flex-col gap-4 text-white">
        <div style={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>
          <span style={{ fontWeight: 700 }}>{activeSlide + 1}</span> / <span>{heroSlides.length}</span>
        </div>
        <div className="flex gap-2 items-center">
          {heroSlides.map((_, i) => (
            <div
              key={i}
              style={{
                height: '2px',
                width: i === activeSlide ? '64px' : '32px',
                background: 'rgba(255,255,255,0.3)',
                cursor: 'pointer',
                overflow: 'hidden',
                transition: 'width 0.3s ease',
              }}
              onClick={() => { setActiveSlide(i); setIsPlaying(false); }}
            >
              {i === activeSlide && isPlaying && (
                <div style={{ height: '100%', background: 'white', animation: 'hero-progress 5s linear forwards' }} />
              )}
              {i === activeSlide && !isPlaying && (
                <div style={{ height: '100%', background: 'white', width: '100%' }} />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-6 mt-1">
          <button onClick={() => { setActiveSlide(p => p === 0 ? heroSlides.length - 1 : p - 1); setIsPlaying(false); }} className="flex items-center gap-2 hover:opacity-70 transition-opacity" style={{ fontFamily: "'Figtree',sans-serif", fontSize: '13px', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
            <PrevIcon /> Prev
          </button>
          <button onClick={() => setIsPlaying(!isPlaying)} className="hover:opacity-70 transition-opacity" style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <button onClick={() => { setActiveSlide(p => (p + 1) % heroSlides.length); setIsPlaying(false); }} className="flex items-center gap-2 hover:opacity-70 transition-opacity" style={{ fontFamily: "'Figtree',sans-serif", fontSize: '13px', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
            Next <NextIcon />
          </button>
        </div>
      </div>



      <style>{`@keyframes hero-progress { from { width: 0%; } to { width: 100%; } }`}</style>
    </div>
  );
};

// ─── Gallery Slider (UNCHANGED) ────────────────────────────────────────────────
const GallerySlider = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [expandedText, setExpandedText] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide(prev => (prev + 1) % gallerySlides.length);
      setExpandedText(false);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="gallery-image-section" className="h-screen w-full relative overflow-hidden bg-black">
      {/* Top gradient for header readability */}
      <div className="absolute inset-0 pointer-events-none z-10" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 30%)' }} />

      {/* Gallery title overlay at top */}
      <div style={{
        position: 'absolute', top: 0, left: 0, width: '100%',
        padding: '88px 48px 0',
        zIndex: 15, textAlign: 'center', pointerEvents: 'none',
      }}>
        <span style={{
          display: 'inline-block',
          fontFamily: "'Figtree',sans-serif",
          fontWeight: 600, fontSize: '11px',
          letterSpacing: '0.1em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.7)',
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.22)',
          padding: '4px 14px', borderRadius: '999px',
          marginBottom: '12px',
        }}>Our Gallery</span>
        <h2 style={{
          fontFamily: "'Fraunces',serif",
          fontWeight: 700,
          fontSize: 'clamp(1.6rem,3.5vw,2.4rem)',
          letterSpacing: '-0.02em',
          color: '#ffffff',
          lineHeight: 1.2,
          margin: '10px 0 0',
          textShadow: '0 2px 12px rgba(0,0,0,0.4)',
        }}>A Glimpse Into What We Stand For</h2>
      </div>

      {gallerySlides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === activeSlide ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'
            }`}
        >
          <div className="absolute inset-0">
            <img src={slide.image} alt={slide.title} className="object-cover w-full h-full" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full md:w-auto md:max-w-xl p-8 pb-16 md:bottom-16 md:left-12">
            <div className="bg-black/30 backdrop-blur-md border border-white/20 p-6 sm:p-8 rounded-xl text-white">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Fraunces',serif" }}>{slide.title}</h2>
                <button
                  aria-label="Toggle details"
                  onClick={() => setExpandedText(!expandedText)}
                  style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', opacity: 0.7, padding: 0 }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transform: expandedText ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
              </div>
              <div style={{ overflow: 'hidden', maxHeight: expandedText ? '200px' : '54px', transition: 'max-height 0.45s ease' }}>
                <p style={{ fontFamily: "'Figtree',sans-serif", color: '#e5e7eb', lineHeight: 1.7, margin: 0 }}>{slide.desc}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-white/20 z-20">
        <div className="h-full bg-white" style={{ animation: 'gallery-progress 4s linear infinite' }} />
      </div>
      <style>{`@keyframes gallery-progress { from { width: 0%; } to { width: 100%; } }`}</style>
    </section>
  );
};

// ─── Team Card ─────────────────────────────────────────────────────────────────
const TeamCard = ({ member, isDark, tokens }) => {
  const { cardBg, cardBorder, cardShadow, headingColor, bodyText, subtleText } = tokens;
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: cardBg,
        border: `1px solid ${hovered ? member.accentColor + '50' : cardBorder}`,
        borderTop: `3px solid ${member.accentColor}`,
        borderRadius: '16px',
        boxShadow: hovered ? `0 16px 48px rgba(26,86,219,0.14), ${cardShadow}` : cardShadow,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header strip */}
      <div style={{
        padding: '28px 28px 20px',
        borderBottom: `1px solid ${cardBorder}`,
        display: 'flex',
        alignItems: 'center',
        gap: '18px',
      }}>
        {/* Avatar */}
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: `${member.accentColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 0 0 4px ${member.accentColor}18`,
        }}>
          <span style={{
            fontFamily: "'Fraunces',serif",
            fontWeight: 700,
            fontSize: '1.2rem',
            color: '#ffffff',
            letterSpacing: '-0.02em',
          }}>{member.initials}</span>
        </div>

        {/* Name / role */}
        <div style={{ minWidth: 0 }}>
          <p style={{
            fontFamily: "'Figtree',sans-serif",
            fontSize: '10.5px',
            fontWeight: 700,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: member.accentColor,
            margin: '0 0 4px',
          }}>{member.dept}</p>
          <h3 style={{
            fontFamily: "'Fraunces',serif",
            fontWeight: 700,
            fontSize: '1.15rem',
            color: headingColor,
            margin: '0 0 3px',
            lineHeight: 1.2,
          }}>{member.name}</h3>
          <p style={{
            fontFamily: "'Figtree',sans-serif",
            fontSize: '12.5px',
            fontWeight: 500,
            color: subtleText,
            margin: 0,
          }}>{member.role}</p>
        </div>
      </div>

      {/* Bio */}
      <div style={{ padding: '20px 28px', flex: 1 }}>
        <div style={{
          width: '28px',
          height: '2px',
          background: member.accentColor,
          borderRadius: '999px',
          marginBottom: '12px',
          opacity: 0.7,
        }} />
        <p style={{
          fontFamily: "'Figtree',sans-serif",
          fontSize: '13.5px',
          lineHeight: 1.78,
          color: bodyText,
          margin: 0,
        }}>{member.bio}</p>
      </div>

      {/* Social links */}
      <div style={{
        padding: '14px 28px 22px',
        display: 'flex',
        gap: '8px',
        borderTop: `1px solid ${cardBorder}`,
      }}>
        {member.social.linkedin && (
          <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer"
            style={{
              width: '32px', height: '32px', borderRadius: '8px',
              border: `1px solid ${cardBorder}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: subtleText, textDecoration: 'none',
              transition: 'color 0.2s, border-color 0.2s, background 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = member.accentColor; e.currentTarget.style.borderColor = `${member.accentColor}50`; e.currentTarget.style.background = `${member.accentColor}10`; }}
            onMouseLeave={e => { e.currentTarget.style.color = subtleText; e.currentTarget.style.borderColor = cardBorder; e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'; }}
          >
            <LinkedInIcon />
          </a>
        )}
        {member.social.email && (
          <a href={`mailto:${member.social.email}`}
            style={{
              width: '32px', height: '32px', borderRadius: '8px',
              border: `1px solid ${cardBorder}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: subtleText, textDecoration: 'none',
              transition: 'color 0.2s, border-color 0.2s, background 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = member.accentColor; e.currentTarget.style.borderColor = `${member.accentColor}50`; e.currentTarget.style.background = `${member.accentColor}10`; }}
            onMouseLeave={e => { e.currentTarget.style.color = subtleText; e.currentTarget.style.borderColor = cardBorder; e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'; }}
          >
            <MailIcon />
          </a>
        )}
      </div>
    </div>
  );
};


// ─── Animated Counter Hook ─────────────────────────────────────────────────
const useCounter = (target, duration = 1800) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setHasStarted(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!hasStarted) return;
    const isFloat = String(target).includes('.');
    const numeric = parseFloat(String(target).replace(/[^0-9.]/g, ''));
    const startTime = performance.now();
    const ease = t => 1 - Math.pow(1 - t, 4);
    const tick = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const val = numeric * ease(p);
      setCount(isFloat ? val.toFixed(1) : Math.floor(val));
      if (p < 1) requestAnimationFrame(tick);
      else setCount(target);
    };
    requestAnimationFrame(tick);
  }, [hasStarted, target, duration]);

  return [ref, count];
};

// ─── Animated Stat Card ────────────────────────────────────────────────────
const StatCard = ({ val, label, tokens, isDark }) => {
  const numeric = parseFloat(String(val).replace(/[^0-9.]/g, ''));
  const trailSuffix = String(val).replace(/[0-9.]/g, '');
  const [ref, count] = useCounter(numeric);
  const { headingColor, subtleText, cardBg, cardBorder, cardShadow } = tokens;

  return (
    <div
      ref={ref}
      style={{
        flex: '1 1 140px',
        textAlign: 'center',
        padding: '28px 16px',
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: '16px',
        boxShadow: cardShadow,
        backdropFilter: 'blur(10px)',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(26,86,219,0.13)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = cardShadow; }}
    >
      <p style={{
        fontFamily: "'Fraunces',serif",
        fontWeight: 700,
        fontSize: '2.2rem',
        color: LOGO_BLUE,
        margin: '0 0 4px',
        lineHeight: 1,
        letterSpacing: '-0.03em',
      }}>
        {count}{trailSuffix}
      </p>
      <p style={{
        fontFamily: "'Figtree',sans-serif",
        fontSize: '12px',
        fontWeight: 500,
        color: subtleText,
        margin: 0,
        letterSpacing: '0.03em',
      }}>{label}</p>
    </div>
  );
};


// ─── Value Row (Typographic Stack + Hover Slide) ───────────────────────────
// ─── Value Row (Responsive — Typographic Stack + Hover Slide) ─────────────
const ValueRow = ({ v, i, isDark, tokens }) => {
  const [hovered, setHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const { headingColor, bodyText, cardBorder } = tokens;

  // On mobile: description always visible below label (no hover needed)
  // On desktop: description slides in from right on hover
  const descVisible = isMobile ? true : hovered;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '28px 0',
        borderBottom: `1px solid ${cardBorder}`,
        borderTop: i === 0 ? `1px solid ${cardBorder}` : 'none',
        cursor: 'default',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Hover background fill — desktop only */}
      {!isMobile && (
        <div style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          background: isDark ? 'rgba(26,86,219,0.06)' : 'rgba(26,86,219,0.04)',
          transform: hovered ? 'translateX(0%)' : 'translateX(-101%)',
          transition: 'transform 0.45s cubic-bezier(0.16,1,0.3,1)',
          pointerEvents: 'none',
          zIndex: 0,
        }} />
      )}

      {/* ── DESKTOP layout: single row, description slides right ── */}
      {!isMobile && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          position: 'relative',
          zIndex: 1,
        }}>
          {/* Left: index + divider + label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: '0 0 auto' }}>
            <span style={{
              fontFamily: "'Figtree',sans-serif",
              fontWeight: 800,
              fontSize: '11px',
              letterSpacing: '0.1em',
              color: hovered ? LOGO_BLUE : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)'),
              transition: 'color 0.35s ease',
              userSelect: 'none',
              flexShrink: 0,
              width: '22px',
              textAlign: 'right',
            }}>
              {String(i + 1).padStart(2, '0')}
            </span>

            <div style={{
              width: '1.5px', height: '32px',
              background: hovered ? LOGO_BLUE : cardBorder,
              borderRadius: '999px', flexShrink: 0,
              transition: 'background 0.35s ease',
            }} />

            <h3 style={{
              fontFamily: "'Fraunces',serif",
              fontWeight: 700,
              fontStyle: hovered ? 'italic' : 'normal',
              fontSize: 'clamp(1.3rem, 2.5vw, 2rem)',
              letterSpacing: '-0.025em',
              lineHeight: 1.15,
              color: hovered ? LOGO_BLUE : headingColor,
              margin: 0,
              transition: 'color 0.35s ease, font-style 0.2s ease',
              whiteSpace: 'nowrap',
            }}>
              {v.label}
            </h3>
          </div>

          {/* Right: sliding description */}
          <p style={{
            fontFamily: "'Figtree',sans-serif",
            fontSize: '14px',
            lineHeight: 1.78,
            color: bodyText,
            margin: 0,
            maxWidth: '420px',
            textAlign: 'right',
            opacity: hovered ? 1 : 0,
            transform: hovered ? 'translateX(0px)' : 'translateX(20px)',
            transition: 'opacity 0.38s ease 0.06s, transform 0.42s cubic-bezier(0.16,1,0.3,1) 0.06s',
            flex: '1 1 0',
          }}>
            {v.desc}
          </p>

          {/* Arrow */}
          <div style={{
            flexShrink: 0,
            width: '30px', height: '30px',
            borderRadius: '50%',
            border: `1.5px solid ${hovered ? LOGO_BLUE : cardBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: hovered ? LOGO_BLUE : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'),
            transform: hovered ? 'rotate(-45deg)' : 'rotate(0deg)',
            transition: 'all 0.35s cubic-bezier(0.16,1,0.3,1)',
          }}>
            <ArrowRightIcon />
          </div>
        </div>
      )}

      {/* ── MOBILE layout: stacked, description always visible ── */}
      {isMobile && (
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Top row: index + divider + label + arrow */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px' }}>
            <span style={{
              fontFamily: "'Figtree',sans-serif",
              fontWeight: 800,
              fontSize: '11px',
              letterSpacing: '0.1em',
              color: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)',
              userSelect: 'none',
              flexShrink: 0,
            }}>
              {String(i + 1).padStart(2, '0')}
            </span>

            <div style={{
              width: '1.5px', height: '28px',
              background: LOGO_BLUE,
              opacity: 0.35,
              borderRadius: '999px', flexShrink: 0,
            }} />

            <h3 style={{
              fontFamily: "'Fraunces',serif",
              fontWeight: 700,
              fontSize: 'clamp(1.15rem, 5vw, 1.45rem)',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              color: headingColor,
              margin: 0,
              flex: 1,
            }}>
              {v.label}
            </h3>

            {/* Static arrow on mobile */}
            <div style={{
              flexShrink: 0,
              width: '26px', height: '26px',
              borderRadius: '50%',
              border: `1.5px solid ${cardBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            }}>
              <ArrowRightIcon />
            </div>
          </div>

          {/* Description — always visible on mobile, indented */}
          <p style={{
            fontFamily: "'Figtree',sans-serif",
            fontSize: '13.5px',
            lineHeight: 1.78,
            color: bodyText,
            margin: 0,
            paddingLeft: '40px', // aligns under the label
          }}>
            {v.desc}
          </p>
        </div>
      )}
    </div>
  );
};


// ─── Team Editorial Row (Magazine Style) ──────────────────────────────────
const TeamEditorialRow = ({ member, i, isDark, tokens }) => {
  const [hovered, setHovered] = useState(false);
  const { headingColor, bodyText, subtleText, cardBorder } = tokens;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderTop: `1px solid ${cardBorder}`,
        padding: '52px 48px',
        margin: '0 -48px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
      }}
    >
      {/* Hover background sweep */}
      <div style={{
        position: 'absolute', inset: 0,
        background: isDark ? 'rgba(26,86,219,0.05)' : 'rgba(26,86,219,0.03)',
        transform: hovered ? 'translateX(0%)' : 'translateX(-101%)',
        transition: 'transform 0.55s cubic-bezier(0.16,1,0.3,1)',
        pointerEvents: 'none', zIndex: 0,
      }} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '48px',
        alignItems: 'flex-start',
        position: 'relative', zIndex: 1,
      }}
        className="team-editorial-grid"
      >
        {/* ── LEFT COLUMN — Index + Name + Role + Socials ── */}
        <div>
          {/* Issue / index label */}
          <p style={{
            fontFamily: "'Figtree',sans-serif",
            fontWeight: 800,
            fontSize: '10.5px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: LOGO_BLUE,
            margin: '0 0 20px',
            opacity: 0.8,
          }}>
            {String(i + 1).padStart(2, '0')} &nbsp;/&nbsp; {member.dept}
          </p>

          {/* Large editorial name */}
          <h3 style={{
            fontFamily: "'Fraunces',serif",
            fontWeight: 700,
            fontStyle: hovered ? 'italic' : 'normal',
            fontSize: 'clamp(2rem, 4vw, 3.2rem)',
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            color: hovered ? LOGO_BLUE : headingColor,
            margin: '0 0 12px',
            transition: 'color 0.35s ease, font-style 0.25s ease',
          }}>
            {member.name}
          </h3>

          {/* Role pill */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '32px',
          }}>
            <div style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: LOGO_BLUE,
              opacity: hovered ? 1 : 0.4,
              transition: 'opacity 0.35s ease',
              flexShrink: 0,
            }} />
            <span style={{
              fontFamily: "'Figtree',sans-serif",
              fontSize: '13px',
              fontWeight: 600,
              color: subtleText,
              letterSpacing: '0.02em',
            }}>
              {member.role}
            </span>
          </div>

          {/* Initials monogram — decorative */}
          <div style={{
            width: '56px', height: '56px',
            borderRadius: '12px',
            background: hovered ? LOGO_BLUE : (isDark ? 'rgba(26,86,219,0.15)' : 'rgba(26,86,219,0.08)'),
            border: `1.5px solid ${hovered ? LOGO_BLUE : 'rgba(26,86,219,0.2)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '28px',
            transition: 'background 0.35s ease, border-color 0.35s ease',
          }}>
            <span style={{
              fontFamily: "'Fraunces',serif",
              fontWeight: 700,
              fontSize: '1.1rem',
              color: hovered ? '#ffffff' : LOGO_BLUE,
              letterSpacing: '-0.02em',
              transition: 'color 0.35s ease',
            }}>
              {member.initials}
            </span>
          </div>

          {/* Social links */}
          <div style={{ display: 'flex', gap: '10px' }}>
            {member.social.linkedin && (
              <a
                href={member.social.linkedin}
                target="_blank" rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '7px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${cardBorder}`,
                  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                  fontFamily: "'Figtree',sans-serif",
                  fontSize: '12px', fontWeight: 600,
                  color: subtleText,
                  textDecoration: 'none',
                  transition: 'color 0.2s, border-color 0.2s, background 0.2s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = LOGO_BLUE;
                  e.currentTarget.style.borderColor = `rgba(26,86,219,0.35)`;
                  e.currentTarget.style.background = `rgba(26,86,219,0.07)`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = subtleText;
                  e.currentTarget.style.borderColor = cardBorder;
                  e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
                }}
              >
                <LinkedInIcon /> LinkedIn
              </a>
            )}
            {member.social.email && (
              <a
                href={`mailto:${member.social.email}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '7px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${cardBorder}`,
                  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                  fontFamily: "'Figtree',sans-serif",
                  fontSize: '12px', fontWeight: 600,
                  color: subtleText,
                  textDecoration: 'none',
                  transition: 'color 0.2s, border-color 0.2s, background 0.2s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = LOGO_BLUE;
                  e.currentTarget.style.borderColor = `rgba(26,86,219,0.35)`;
                  e.currentTarget.style.background = `rgba(26,86,219,0.07)`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = subtleText;
                  e.currentTarget.style.borderColor = cardBorder;
                  e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
                }}
              >
                <MailIcon /> Email
              </a>
            )}
          </div>
        </div>

        {/* ── RIGHT COLUMN — Bio ── */}
        <div style={{ paddingTop: '4px' }}>
          {/* Decorative quote mark */}
          <div style={{
            fontFamily: "'Fraunces',serif",
            fontSize: '4rem',
            lineHeight: 0.8,
            color: LOGO_BLUE,
            opacity: 0.15,
            marginBottom: '8px',
            userSelect: 'none',
          }}>"</div>

          <p style={{
            fontFamily: "'Figtree',sans-serif",
            fontSize: 'clamp(14px, 1.5vw, 15.5px)',
            lineHeight: 1.88,
            color: bodyText,
            margin: '0 0 32px',
          }}>
            {member.bio}
          </p>

          {/* Thin ruled line with dept tag */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '14px',
          }}>
            <div style={{
              flex: 1, height: '1px',
              background: hovered ? `rgba(26,86,219,0.25)` : cardBorder,
              transition: 'background 0.4s ease',
            }} />
            <span style={{
              fontFamily: "'Figtree',sans-serif",
              fontSize: '10.5px',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: hovered ? LOGO_BLUE : subtleText,
              transition: 'color 0.35s ease',
            }}>
              {member.dept}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};


// ─── Main Page ─────────────────────────────────────────────────────────────────
const OurTeam = () => {
  const { isDark } = useTheme();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Make header transparent ONLY when an image section is physically behind the header
  useEffect(() => {
    const HEADER_H = 72;
    const dispatch = (transparent) =>
      window.dispatchEvent(new CustomEvent('header-transparency', { detail: { transparent } }));
    const check = () => {
      const isUnderHeader = (id) => {
        const el = document.getElementById(id);
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return r.top <= HEADER_H && r.bottom > HEADER_H;
      };
      dispatch(isUnderHeader('hero-image-section') || isUnderHeader('gallery-image-section'));
    };
    check(); // run immediately so hero page-load is transparent
    window.addEventListener('scroll', check, { passive: true });
    return () => { window.removeEventListener('scroll', check); dispatch(false); };
  }, []);

  // ── Hero section snap: slow eased scroll when scrolling down from hero ───────
  useEffect(() => {
    // Snap is a desktop-only feature. On touch/mobile devices the browser's
    // native touch-scroll handles the gallery section correctly on its own.
    // Detecting both viewport width AND touch capability covers real devices
    // as well as browser DevTools responsive simulation.
    const isMobile = { current: window.innerWidth < 1024 || 'ontouchstart' in window };
    const onResize = () => {
      isMobile.current = window.innerWidth < 1024 || 'ontouchstart' in window;
    };
    window.addEventListener('resize', onResize, { passive: true });

    let snapping   = false;
    const DURATION = 2000; // ms - smooth premium feel

    const easedScrollTo = (targetY) => {
      if (snapping) return;
      snapping = true;
      const startY   = window.scrollY;
      const distance = targetY - startY;
      if (Math.abs(distance) < 4) { snapping = false; return; }
      const startTime = performance.now();
      const ease = (t) => t < 0.5 ? 8*t*t*t*t : 1 - Math.pow(-2*t+2,4)/2;
      const step = (now) => {
        const p = Math.min((now - startTime) / DURATION, 1);
        window.scrollTo(0, startY + distance * ease(p));
        if (p < 1) requestAnimationFrame(step);
        else setTimeout(() => { snapping = false; }, 200);
      };
      requestAnimationFrame(step);
    };

    const snapToEl = (id) => {
      const el = document.getElementById(id);
      if (!el) return;
      easedScrollTo(el.getBoundingClientRect().top + window.scrollY);
    };

    const onWheel = (e) => {
      // On mobile/touch viewports let the browser handle scrolling natively.
      if (isMobile.current) return;

      const vh   = window.innerHeight;
      const down = e.deltaY > 0;

      // ── Hero: scrolling DOWN while hero is visible → snap to intro ─────────
      const hero = document.getElementById('hero-image-section');
      if (hero) {
        const r = hero.getBoundingClientRect();
        const onHero = r.bottom > vh * 0.35 && r.top > -vh * 0.9;
        if (onHero) {
          if (snapping) { e.preventDefault(); return; }
          if (down) { e.preventDefault(); snapToEl('team'); }
          return; // stop here - don't fall through to gallery check
        }
      }

      // ── Gallery approach: snap TO gallery when entering from below ─────────
      // ── Gallery exit:     snap TO how-we-work when on gallery & scroll DOWN ─
      const gallery = document.getElementById('gallery-image-section');
      if (gallery) {
        const r = gallery.getBoundingClientRect();

        // Entering from below - gallery top is between header and viewport bottom
        const approaching = down && r.top > 0 && r.top < vh;

        // On gallery - gallery top is at or above the header line
        const onGallery = r.top <= 72 && r.bottom > 0;

        if (approaching) {
          if (snapping) { e.preventDefault(); return; }
          e.preventDefault();
          snapToEl('gallery-image-section');
          return;
        }

        if (onGallery && down) {
          if (snapping) { e.preventDefault(); return; }
          e.preventDefault();
          snapToEl('how-we-work-section');
        }
      }
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Theme tokens
  const headingColor = isDark ? '#f1f5f9' : '#0f172a';
  const bodyText = isDark ? '#94a3b8' : '#475569';
  const subtleText = isDark ? '#64748b' : '#94a3b8';
  const cardBg = isDark ? 'rgba(15,23,42,0.85)' : '#ffffff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
  const cardShadow = isDark ? '0 4px 24px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.07)';
  const pageBg = isDark ? '#020617' : '#f8faff';
  const altBg = isDark ? '#0a1020' : '#f1f5f9';
  const plainBg = isDark ? '#020617' : '#ffffff';
  const divider = isDark ? 'rgba(255,255,255,0.07)' : '#e2e8f0';
  const badgeBg = isDark ? 'rgba(26,86,219,0.15)' : 'rgba(26,86,219,0.07)';
  const badgeBorder = isDark ? 'rgba(26,86,219,0.35)' : 'rgba(26,86,219,0.2)';

  const tokens = { cardBg, cardBorder, cardShadow, headingColor, bodyText, subtleText };

  const dotGrid = {
    position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
    backgroundImage: `radial-gradient(${isDark ? 'rgba(148,163,184,0.05)' : 'rgba(0,0,0,0.035)'} 1px, transparent 1px)`,
    backgroundSize: '22px 22px',
  };

  // Reusable section header components
  const Badge = ({ children }) => (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
      <span style={{
        fontFamily: "'Figtree',sans-serif",
        fontWeight: 600,
        fontSize: '11px',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: LOGO_BLUE,
        background: badgeBg,
        border: `1px solid ${badgeBorder}`,
        padding: '4px 14px',
        borderRadius: '999px',
      }}>{children}</span>
    </div>
  );

  const SectionTitle = ({ children }) => (
    <h2 style={{
      fontFamily: "'Fraunces',serif",
      fontWeight: 700,
      fontSize: 'clamp(1.75rem,3.5vw,2.6rem)',
      letterSpacing: '-0.02em',
      color: headingColor,
      textAlign: 'center',
      marginBottom: '48px',
      lineHeight: 1.18,
    }}>{children}</h2>
  );

  return (
    <>
      <Seo
        title="Our Team - TotoWala | The People Behind India's Ride App"
        description="Meet the passionate team at TotoWala Technologies building India's best ride booking platform. Our founders and team are committed to transforming transportation across India."
        canonical="/our-team"
        schemaMarkup={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'TotoWala Team',
          description: 'Meet the team at TotoWala Technologies Private Limited.',
          url: 'https://totowala.in/our-team',
        }}
      />
    <div style={{ minHeight: '100vh', background: pageBg }}>
      <HeaderSection />

      {/* ══ 1. HERO SLIDER - negative margin pulls it up behind the sticky header ══ */}
      {/* marginTop: -70px makes the 100vh hero start at y=0 so header floats over it */}
      <div style={{ marginTop: '-70px' }}>
        <HeroSlider />
      </div>

      {/* ══ 2. INTRO - Who We Are (Redesigned) ══ */}
      <section id="team" style={{ background: plainBg, padding: '96px 16px', position: 'relative', overflow: 'hidden' }}>
        <div style={dotGrid} />

        {/* Soft background glow blob */}
        <div style={{
          position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)',
          width: '600px', height: '400px', borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(ellipse, rgba(26,86,219,0.08) 0%, transparent 70%)'
            : 'radial-gradient(ellipse, rgba(26,86,219,0.06) 0%, transparent 70%)',
          pointerEvents: 'none', zIndex: 0,
        }} />

        <div className="relative max-w-4xl mx-auto" style={{ zIndex: 1 }}>

          {/* Badge */}
          <FadeIn>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
              <Badge>The People</Badge>
            </div>
          </FadeIn>

          {/* ── Founder Quote ── */}
          <FadeIn delay={0.07}>
            <div style={{
              textAlign: 'center',
              marginBottom: '32px',
              position: 'relative',
              padding: '0 24px',
            }}>
              {/* Large decorative quote mark */}
              <div style={{
                fontFamily: "'Fraunces',serif",
                fontSize: '8rem',
                lineHeight: 0.6,
                color: LOGO_BLUE,
                opacity: 0.12,
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                userSelect: 'none',
                pointerEvents: 'none',
              }}>"</div>

              <blockquote style={{
                fontFamily: "'Fraunces',serif",
                fontStyle: 'italic',
                fontWeight: 600,
                fontSize: 'clamp(1.45rem, 3.2vw, 2.1rem)',
                lineHeight: 1.42,
                color: headingColor,
                margin: '0 auto',
                maxWidth: '720px',
                letterSpacing: '-0.02em',
                position: 'relative',
                zIndex: 1,
              }}>
                We built TotoWala because People deserved better - fair rides, honest fares, and a platform that actually puts drivers first.
              </blockquote>

              {/* Attribution */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginTop: '20px',
              }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  background: LOGO_BLUE,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{ fontFamily: "'Fraunces',serif", fontWeight: 700, fontSize: '0.75rem', color: '#fff' }}>NR</span>
                </div>
                <p style={{
                  fontFamily: "'Figtree',sans-serif",
                  fontSize: '13px',
                  fontWeight: 600,
                  color: subtleText,
                  margin: 0,
                  letterSpacing: '0.04em',
                }}>
                  Namit Rai &nbsp;·&nbsp; Founder, TotoWala
                </p>
              </div>
            </div>
          </FadeIn>

          {/* ── Divider ── */}
          <FadeIn delay={0.13}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '16px',
              maxWidth: '480px', margin: '0 auto 32px',
            }}>
              <div style={{ flex: 1, height: '1px', background: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0' }} />
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: LOGO_BLUE, opacity: 0.5 }} />
              <div style={{ flex: 1, height: '1px', background: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0' }} />
            </div>
          </FadeIn>

          {/* ── Mission Statement ── */}
          <FadeIn delay={0.17}>
            <p style={{
              fontFamily: "'Figtree',sans-serif",
              fontSize: 'clamp(1rem, 2vw, 1.08rem)',
              lineHeight: 1.82,
              color: bodyText,
              textAlign: 'center',
              maxWidth: '580px',
              margin: '0 auto 52px',
            }}>
              A Bihar-born startup with lowest-commission rides, no hidden fees, and a team that measures success in driver livelihoods - not revenue charts.
            </p>
          </FadeIn>

          {/* ── Animated Stat Cards ── */}
          <FadeIn delay={0.22}>
            <div style={{
              display: 'flex',
              gap: '14px',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}>
              {[
                { val: '2', label: 'Core Team Members' },
                { val: '1', label: 'Clear Mission' },
                { val: '1500+', label: 'Drivers Onboarded' },
                { val: '4.8', label: 'App Rating' },
              ].map((s, i) => (
                <StatCard key={i} val={s.val} label={s.label} tokens={tokens} isDark={isDark} />
              ))}
            </div>
          </FadeIn>

        </div>
      </section>

      {/* ══ 3. TEAM MEMBERS (Magazine Editorial) ══ */}
      <section style={{ background: altBg, padding: '88px 16px', position: 'relative', overflow: 'hidden' }}>
        <style>{`
          @media (max-width: 640px) {
            .team-editorial-grid {
              grid-template-columns: 1fr !important;
              gap: 28px !important;
            }
          }
        `}</style>
        <div style={dotGrid} />

        {/* Ambient top-right glow */}
        <div style={{
          position: 'absolute', top: '-60px', right: '-60px',
          width: '420px', height: '420px', borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(ellipse, rgba(26,86,219,0.07) 0%, transparent 70%)'
            : 'radial-gradient(ellipse, rgba(26,86,219,0.05) 0%, transparent 70%)',
          pointerEvents: 'none', zIndex: 0,
        }} />

        <div className="relative max-w-5xl mx-auto" style={{ zIndex: 1 }}>
          <FadeIn>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              <Badge>Our People</Badge>
            </div>
          </FadeIn>
          <FadeIn delay={0.07}>
            <SectionTitle>The Founding Team</SectionTitle>
          </FadeIn>

          {/* Editorial rows */}
          <div style={{ marginTop: '16px' }}>
            {TEAM.map((member, i) => (
              <FadeIn key={i} delay={i * 0.09}>
                <TeamEditorialRow
                  member={member}
                  i={i}
                  isDark={isDark}
                  tokens={tokens}
                />
              </FadeIn>
            ))}
            {/* Bottom border rule */}
            <div style={{ borderTop: `1px solid ${cardBorder}`, margin: '0 -48px' }} />
          </div>
        </div>
      </section>

      {/* ══ 4. OUR VALUES (Redesigned — Typographic Stack + Hover Slide) ══ */}
      <section id="values-section" style={{ background: plainBg, padding: '96px 16px', position: 'relative', overflow: 'hidden' }}>
        <div style={dotGrid} />

        {/* Ambient glow */}
        <div style={{
          position: 'absolute', bottom: '-100px', right: '-80px',
          width: '500px', height: '500px', borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(ellipse, rgba(26,86,219,0.07) 0%, transparent 70%)'
            : 'radial-gradient(ellipse, rgba(26,86,219,0.05) 0%, transparent 70%)',
          pointerEvents: 'none', zIndex: 0,
        }} />

        <div className="relative max-w-5xl mx-auto" style={{ zIndex: 1 }}>
          <FadeIn>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              <Badge>What We Stand For</Badge>
            </div>
          </FadeIn>
          <FadeIn delay={0.07}>
            <SectionTitle>Our Team's Core Values</SectionTitle>
          </FadeIn>

          {/* Value Rows */}
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: '8px' }}>
            {VALUES.map((v, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <ValueRow v={v} i={i} isDark={isDark} tokens={{ headingColor, bodyText, subtleText, cardBorder }} />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 5. GALLERY ══ */}
      <GallerySlider />

      {/* ══ 6. HOW WE WORK ══ */}
      <section
        id="how-we-work-section"
        style={{
          background: plainBg,
          padding: '96px 16px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={dotGrid} />

        <div
          className="relative max-w-6xl mx-auto"
          style={{ zIndex: 1, position: 'relative' }}
        >
          {/* Header */}
          <FadeIn>
            <Badge>Our Culture</Badge>
          </FadeIn>

          <FadeIn delay={0.07}>
            <SectionTitle>How We Work</SectionTitle>
          </FadeIn>

          <FadeIn delay={0.12}>
            <p
              style={{
                maxWidth: '660px',
                margin: '14px auto 42px',
                textAlign: 'center',
                fontSize: '15px',
                lineHeight: 1.8,
                color: bodyText,
                opacity: 0.85,
              }}
            >
              A clear, efficient process designed to deliver quality outcomes
              with speed, transparency, and precision.
            </p>
          </FadeIn>

          {/* Cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '24px',
              alignItems: 'stretch',
            }}
          >
            {HOW_WE_WORK.map((step, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div
                  style={{
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    borderRadius: '18px',
                    padding: '36px 28px',
                    boxShadow: cardShadow,
                    minHeight: '320px',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    transition:
                      'transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform =
                      'translateY(-8px) scale(1.015)';
                    e.currentTarget.style.boxShadow =
                      '0 20px 45px rgba(26,86,219,0.10)';
                    e.currentTarget.style.borderColor =
                      'rgba(26,86,219,0.22)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = cardShadow;
                    e.currentTarget.style.borderColor = cardBorder;
                  }}
                >
                  {/* Top Accent Line */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background:
                        'linear-gradient(90deg,#1A56DB,#3B82F6,#60A5FA)',
                    }}
                  />

                  {/* Watermark Number */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '14px',
                      right: '18px',
                      fontFamily: "'Fraunces', serif",
                      fontWeight: 800,
                      fontSize: '5.5rem',
                      lineHeight: 1,
                      color: 'transparent',
                      WebkitTextStroke: '1px rgba(26,86,219,0.12)',
                      opacity: 0.85,
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  >
                    {step.step}
                  </div>

                  {/* Small Step Badge */}
                  <div
                    style={{
                      display: 'inline-block',
                      fontFamily: "'Figtree', sans-serif",
                      fontWeight: 800,
                      fontSize: '11px',
                      letterSpacing: '0.08em',
                      color: LOGO_BLUE,
                      background: badgeBg,
                      border: `1px solid ${badgeBorder}`,
                      padding: '4px 11px',
                      borderRadius: '7px',
                      marginBottom: '18px',
                      position: 'relative',
                      zIndex: 2,
                    }}
                  >
                    {step.step}
                  </div>

                  {/* Title */}
                  <h3
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontWeight: 700,
                      fontSize: '1.22rem',
                      letterSpacing: '-0.02em',
                      color: headingColor,
                      marginBottom: '12px',
                      position: 'relative',
                      zIndex: 2,
                    }}
                  >
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p
                    style={{
                      fontFamily: "'Figtree', sans-serif",
                      fontSize: '14px',
                      lineHeight: 1.85,
                      color: bodyText,
                      opacity: 0.9,
                      margin: 0,
                      position: 'relative',
                      zIndex: 2,
                    }}
                  >
                    {step.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 8. JOIN OUR TEAM ══ */}
      <section style={{ background: altBg, padding: '96px 16px', position: 'relative', overflow: 'hidden' }}>
        <div style={dotGrid} />

        <div className="relative max-w-6xl mx-auto" style={{ zIndex: 1 }}>

          {/* ── Culture banner ── */}
          <FadeIn>
            <div style={{
              background: `linear-gradient(135deg, ${LOGO_BLUE} 0%, #1e40af 50%, #1d4ed8 100%)`,
              borderRadius: '24px',
              padding: 'clamp(36px, 6vw, 64px) clamp(28px, 6vw, 64px)',
              marginBottom: '56px',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* subtle grid overlay */}
              <div style={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
                backgroundSize: '22px 22px',
              }} />
              {/* glow orb */}
              <div style={{
                position: 'absolute', top: '-60px', right: '-60px',
                width: '280px', height: '280px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.06)', pointerEvents: 'none',
              }} />

              <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Badge */}
                <div style={{ marginBottom: '20px' }}>
                  <span style={{
                    fontFamily: "'Figtree',sans-serif", fontWeight: 600,
                    fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.25)', padding: '4px 14px', borderRadius: '999px',
                  }}>We're Hiring</span>
                </div>

                {/* Headline */}
                <h2 style={{
                  fontFamily: "'Fraunces',serif", fontWeight: 700,
                  fontSize: 'clamp(1.9rem, 4vw, 3rem)', letterSpacing: '-0.025em',
                  color: '#ffffff', lineHeight: 1.15, marginBottom: '16px',
                  maxWidth: '600px',
                }}>
                  Build Bihar's Future<br />with Us
                </h2>

                {/* Founder quote */}
                <p style={{
                  fontFamily: "'Figtree',sans-serif", fontSize: 'clamp(13.5px, 1.5vw, 15px)',
                  color: 'rgba(255,255,255,0.75)', lineHeight: 1.75,
                  maxWidth: '520px', marginBottom: '36px',
                }}>
                  "We are a small, ambitious team with outsized impact. Every person here shapes the product directly -
                  just real work that changes how Bihar moves."
                  <span style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: 'rgba(255,255,255,0.5)', fontWeight: 600, letterSpacing: '0.04em' }}>
                    - Namit Rai, Founder
                  </span>
                </p>

                {/* Stat chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {[
                    { value: 'Day 1',      label: 'Ground Floor Opportunity' },
                    { value: '38',         label: 'Districts to Serve' },
                    { value: 'Full',       label: 'Ownership of Your Work' },
                    { value: ' Collaborative', label: 'Environment' },
                  ].map((stat, i) => (
                    <div key={i} style={{
                      background: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '12px',
                      padding: '10px 18px',
                      textAlign: 'center',
                    }}>
                      <div style={{ fontFamily: "'Fraunces',serif", fontWeight: 700, fontSize: '1.25rem', color: '#ffffff', lineHeight: 1 }}>{stat.value}</div>
                      <div style={{ fontFamily: "'Figtree',sans-serif", fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginTop: '3px', fontWeight: 500 }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>

          {/* ── Perks grid ── */}
          <FadeIn delay={0.06}>
            <h3 style={{
              fontFamily: "'Fraunces',serif", fontWeight: 700,
              fontSize: 'clamp(1.35rem, 2.5vw, 1.65rem)', color: headingColor,
              marginBottom: '20px', letterSpacing: '-0.015em',
            }}>Why Join TotoWala?</h3>
          </FadeIn>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'auto auto',
            gap: '16px',
            marginBottom: '56px',
          }}>
            {PERKS.map((perk, i) => (
              <FadeIn key={i} delay={0.07 + i * 0.04} style={{ height: '100%' }}>
                <div style={{
                  background: cardBg,
                  border: `1px solid ${cardBorder}`,
                  borderRadius: '16px',
                  padding: '26px 24px',
                  boxShadow: cardShadow,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  boxSizing: 'border-box',
                  transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 32px rgba(26,86,219,0.1)`; e.currentTarget.style.borderColor = `rgba(26,86,219,0.25)`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = cardShadow; e.currentTarget.style.borderColor = cardBorder; }}
                >
                  <div style={{ width: '28px', height: '3px', borderRadius: '2px', background: `linear-gradient(90deg, ${LOGO_BLUE}, #1d4ed8)`, marginBottom: '16px', flexShrink: 0 }} />
                  <div style={{ fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: '15px', color: headingColor, marginBottom: '8px' }}>{perk.label}</div>
                  <div style={{ fontFamily: "'Figtree',sans-serif", fontSize: '14px', color: subtleText, lineHeight: 1.7, flex: 1 }}>{perk.desc}</div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* ── Open roles table ── */}
          <FadeIn delay={0.1}>
            <h3 style={{
              fontFamily: "'Fraunces',serif", fontWeight: 700,
              fontSize: 'clamp(1.35rem, 2.5vw, 1.65rem)', color: headingColor,
              marginBottom: '20px', letterSpacing: '-0.015em',
            }}>Open Positions</h3>
          </FadeIn>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '40px' }}>
            {OPEN_ROLES.map((role, i) => (
              <FadeIn key={i} delay={0.12 + i * 0.04}>
                <div style={{
                  background: cardBg,
                  border: `1px solid ${cardBorder}`,
                  borderRadius: '14px',
                  padding: '20px 24px',
                  boxShadow: cardShadow,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  cursor: 'pointer',
                  transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease',
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateX(6px)';
                    e.currentTarget.style.boxShadow = `0 8px 28px rgba(26,86,219,0.1)`;
                    e.currentTarget.style.borderColor = `rgba(26,86,219,0.3)`;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = cardShadow;
                    e.currentTarget.style.borderColor = cardBorder;
                  }}
                >
                  {/* left accent bar */}
                  <div style={{
                    width: '3px', height: '40px', borderRadius: '2px',
                    background: `linear-gradient(180deg, ${LOGO_BLUE}, #1d4ed8)`,
                    flexShrink: 0,
                  }} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{ fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: '15px', color: headingColor, margin: '0 0 6px' }}>{role.title}</h4>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{
                        fontFamily: "'Figtree',sans-serif", fontSize: '12px', fontWeight: 600,
                        color: LOGO_BLUE, background: badgeBg, border: `1px solid ${badgeBorder}`,
                        padding: '2px 10px', borderRadius: '999px',
                      }}>{role.dept}</span>
                      <span style={{
                        fontFamily: "'Figtree',sans-serif", fontSize: '12px', fontWeight: 500,
                        color: subtleText,
                      }}>{role.type}</span>
                      <span style={{
                        fontFamily: "'Figtree',sans-serif", fontSize: '12px', color: subtleText,
                        display: 'flex', alignItems: 'center', gap: '3px',
                      }}>· Bihar, India</span>
                    </div>
                  </div>

                  <div style={{
                    flexShrink: 0, color: LOGO_BLUE,
                    background: badgeBg, border: `1px solid ${badgeBorder}`,
                    borderRadius: '8px', padding: '6px 8px',
                    display: 'flex', alignItems: 'center',
                  }}>
                    <ArrowRightIcon />
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* ── CTA strip ── */}
          <FadeIn delay={0.22}>
            <div style={{
              background: cardBg,
              border: `1px solid ${cardBorder}`,
              borderRadius: '20px',
              padding: '32px 36px',
              boxShadow: cardShadow,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '20px',
            }}>
              <div>
                <h4 style={{ fontFamily: "'Fraunces',serif", fontWeight: 700, fontSize: '1.15rem', color: headingColor, margin: '0 0 6px', letterSpacing: '-0.01em' }}>
                  Don't see your role?
                </h4>
                <p style={{ fontFamily: "'Figtree',sans-serif", fontSize: '13.5px', color: bodyText, margin: 0, lineHeight: 1.6 }}>
                  We're always looking for sharp, passionate people. Send us your profile and we'll reach out when the right opportunity opens up.
                </p>
              </div>
              <a href="mailto:contact@totowala.in"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '9px',
                  background: LOGO_BLUE, color: '#ffffff', textDecoration: 'none',
                  borderRadius: '12px', padding: '13px 24px',
                  fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: '14px',
                  boxShadow: '0 4px 18px rgba(26,86,219,0.3)',
                  whiteSpace: 'nowrap',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(26,86,219,0.42)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 18px rgba(26,86,219,0.3)'; }}
              >
                <MailIcon /> Send Your Application
              </a>
            </div>
          </FadeIn>

        </div>
      </section>

      {/* ══ 9. CLOSING CTA - Logo Blue background ══ */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: '88px 16px', background: LOGO_BLUE }}>
        {/* Subtle dot grid overlay */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }} />
        {/* Soft radial glow */}
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '320px', height: '320px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', filter: 'blur(60px)', pointerEvents: 'none', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '280px', height: '280px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)', filter: 'blur(60px)', pointerEvents: 'none', zIndex: 0 }} />

        <FadeIn>
          <div className="relative max-w-2xl mx-auto text-center" style={{ zIndex: 1 }}>

            {/* Live badge */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '5px 16px', borderRadius: '999px',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.22)',
                fontFamily: "'Figtree',sans-serif",
                fontWeight: 600, fontSize: '11.5px',
                letterSpacing: '0.07em', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.9)',
              }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f59e0b', boxShadow: '0 0 6px rgba(245,158,11,0.7)', flexShrink: 0 }} />
                Started in Bihar · Expanding across India
              </span>
            </div>

            <h2 style={{
              fontFamily: "'Fraunces',serif",
              fontWeight: 700,
              fontSize: 'clamp(1.9rem,5vw,3rem)',
              letterSpacing: '-0.025em',
              color: '#ffffff',
              marginBottom: '16px',
              lineHeight: 1.15,
            }}>
              Be Part of Our Story
            </h2>

            <p style={{
              fontFamily: "'Figtree',sans-serif",
              fontSize: 'clamp(1rem,2.2vw,1.1rem)',
              lineHeight: 1.78,
              color: 'rgba(255,255,255,0.75)',
              maxWidth: '520px',
              margin: '0 auto 36px',
            }}>
              Whether you are a rider, a driver, or someone who wants to build with us - TotoWala welcomes you.
              Together, we are reshaping how Bhagalpur moves.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
              <a href="https://play.google.com/store/apps/details?id=com.elt.totowala"
                target="_blank" rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '9px',
                  padding: '13px 26px', borderRadius: '10px',
                  background: '#ffffff', color: LOGO_BLUE,
                  textDecoration: 'none',
                  fontFamily: "'Figtree',sans-serif",
                  fontWeight: 700, fontSize: '14px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.28)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.18)'; }}
              >
                <DownloadIcon /> Download for Riders
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.elt.totowalafordrivers"
                target="_blank" rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '9px',
                  padding: '13px 26px', borderRadius: '10px',
                  background: 'rgba(255,255,255,0.12)',
                  border: '1.5px solid rgba(255,255,255,0.28)',
                  color: '#ffffff',
                  textDecoration: 'none',
                  fontFamily: "'Figtree',sans-serif",
                  fontWeight: 600, fontSize: '14px',
                  backdropFilter: 'blur(8px)',
                  transition: 'transform 0.2s ease, background 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
              >
                <DownloadIcon /> Download for Drivers
              </a>
            </div>

            <p style={{
              fontFamily: "'Figtree',sans-serif",
              fontSize: '12px',
              color: 'rgba(255,255,255,0.4)',
              margin: '28px 0 0',
              letterSpacing: '0.04em',
            }}>
              Bhagalpur &nbsp;·&nbsp; Bihar &nbsp;·&nbsp; India
            </p>
          </div>
        </FadeIn>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
    </>
  );
};

export default OurTeam;
